// Pupil Profile Tab - SAT V4 Zone
import { DsButton, DsCard, DsIcon, DsTypography } from '@ice-web-app/designSystem';
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import type { Pupil, YearGroup } from '../../../../types';

interface ProfileTabProps {
  pupil: Pupil;
  onUpdate?: (updates: Partial<Pupil>) => void;
}

const statusColors: Record<string, string> = {
  active: '#4CAF50',
  inactive: '#9E9E9E',
  transferred: '#FF9800',
};

const STATUSES = ['active', 'inactive', 'transferred'] as const;
const YEAR_GROUPS: YearGroup[] = [7, 8, 9, 10, 11, 12, 13];
const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];

const ProfileTab: FC<ProfileTabProps> = ({ pupil, onUpdate }) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: pupil.firstName,
    lastName: pupil.lastName,
    gender: pupil.gender || '',
    yearGroup: pupil.yearGroup,
    classId: pupil.classId,
    status: pupil.status,
  });

  const handleOpenEdit = () => {
    setFormData({
      firstName: pupil.firstName,
      lastName: pupil.lastName,
      gender: pupil.gender || '',
      yearGroup: pupil.yearGroup,
      classId: pupil.classId,
      status: pupil.status,
    });
    setEditModalOpen(true);
  };

  const handleSave = () => {
    onUpdate?.(formData);
    setEditModalOpen(false);
  };
  return (
    <Stack spacing={3}>
      {/* Header Card */}
      <DsCard sx={{ p: 3 }}>
        <Stack direction="row" spacing={3} alignItems="center">
          <Avatar
            sx={{
              width: 100,
              height: 100,
              bgcolor: 'success.main',
              color: 'white',
              fontSize: 32,
            }}
          >
            {pupil.firstName[0]}
            {pupil.lastName[0]}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <DsTypography variant="h5" fontWeight={700}>
              {pupil.firstName} {pupil.lastName}
            </DsTypography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip label={`Year ${pupil.yearGroup}`} color="success" />
              <Chip
                label={pupil.status}
                sx={{
                  bgcolor: `${statusColors[pupil.status]}20`,
                  color: statusColors[pupil.status],
                  textTransform: 'capitalize',
                }}
              />
              <Chip label={pupil.studentId} variant="outlined" />
            </Stack>
          </Box>
          {onUpdate && (
            <DsButton variant="contained" startIcon={<DsIcon icon="EDIT" />} onClick={handleOpenEdit}>
              Edit Profile
            </DsButton>
          )}
        </Stack>
      </DsCard>

      {/* Personal Information */}
      <DsCard sx={{ p: 3 }}>
        <DsTypography variant="h6" fontWeight={600} gutterBottom>
          Personal Information
        </DsTypography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={pupil.firstName}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={pupil.lastName}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date of Birth"
              value={new Date(pupil.dateOfBirth).toLocaleDateString()}
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Gender"
              value={pupil.gender || 'Not specified'}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
            />
          </Grid>
        </Grid>
      </DsCard>

      {/* Enrollment Information */}
      <DsCard sx={{ p: 3 }}>
        <DsTypography variant="h6" fontWeight={600} gutterBottom>
          Enrollment Information
        </DsTypography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Student ID"
              value={pupil.studentId}
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Year Group"
              value={`Year ${pupil.yearGroup}`}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Form Group"
              value={pupil.classId}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Enrollment Date"
              value={new Date(pupil.enrollmentDate).toLocaleDateString()}
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Status"
              value={pupil.status}
              size="small"
              InputProps={{ readOnly: !onUpdate }}
              sx={{ textTransform: 'capitalize' }}
            />
          </Grid>
        </Grid>
      </DsCard>

      {/* Quick Stats */}
      <DsCard sx={{ p: 3 }}>
        <DsTypography variant="h6" fontWeight={600} gutterBottom>
          Quick Stats
        </DsTypography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <DsTypography variant="h4" fontWeight={700} color="primary.main">
                {pupil.subjects.length}
              </DsTypography>
              <DsTypography variant="caption" color="text.secondary">
                Subjects
              </DsTypography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <DsTypography variant="h4" fontWeight={700} color="secondary.main">
                {pupil.parentCarerIds?.length || 0}
              </DsTypography>
              <DsTypography variant="caption" color="text.secondary">
                Parents/Carers
              </DsTypography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <DsTypography variant="h4" fontWeight={700} color="info.main">
                {pupil.specialRequirements?.length || 0}
              </DsTypography>
              <DsTypography variant="caption" color="text.secondary">
                Special Requirements
              </DsTypography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <DsTypography variant="h4" fontWeight={700} color="success.main">
                {Math.floor((Date.now() - new Date(pupil.enrollmentDate).getTime()) / (1000 * 60 * 60 * 24 * 365))}
              </DsTypography>
              <DsTypography variant="caption" color="text.secondary">
                Years Enrolled
              </DsTypography>
            </Box>
          </Grid>
        </Grid>
      </DsCard>

      {/* Edit Profile Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Pupil Profile</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </Grid>
            </Grid>
            <FormControl fullWidth size="small">
              <InputLabel>Gender</InputLabel>
              <Select
                value={formData.gender}
                label="Gender"
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              >
                {GENDERS.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Year Group</InputLabel>
                  <Select
                    value={formData.yearGroup}
                    label="Year Group"
                    onChange={(e) => setFormData({ ...formData, yearGroup: e.target.value as YearGroup })}
                  >
                    {YEAR_GROUPS.map((y) => (
                      <MenuItem key={y} value={y}>
                        Year {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Class ID"
                  value={formData.classId}
                  onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
                />
              </Grid>
            </Grid>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value as Pupil['status'] })}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <DsButton variant="outlined" onClick={() => setEditModalOpen(false)}>
            Cancel
          </DsButton>
          <DsButton variant="contained" onClick={handleSave}>
            Save Changes
          </DsButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ProfileTab;
